import { request, windowQuery, type DateWindow } from './client'
import type { StaffAuth } from './teaching'
/* ⚠️ The subscription a school is created with is F3's own row type, imported
   rather than restated — see `CreatedSchool.subscription`. */
import type { SubscriptionRow } from './billing'
/* One child's five-game fold is the same shape every other tier reads — see
   `games.ts`. Re-declaring it here would be the second definition that
   eventually disagrees with the table it sits inside. */
import type { GameStat, GameSummary } from './games'
import type {
  AuditAction,
  ContentState,
  Region,
  SchoolStatus,
} from '../types/prisma-enums'
/* P1-47 — the one VALUE import: every audited action is filterable, read from the generated enum mirror. */
import { AuditAction as AUDIT_ACTION_VALUES } from '../types/prisma-enums'

/**
 * F1, F2, F4, F5 and X1/X6 — the platform tier's wire.
 *
 * 🔴 EVERY CALL SENDS `context: 'user'` WITH A SUPER_ADMIN TOKEN, AND THE MODULE
 * STORES NOTHING — `teaching.ts`, `parent.ts` and `school.ts`'s shape exactly:
 * the caller supplies the token.
 *
 * 🔴 THERE IS NO FIXTURE IN THIS FILE AND NONE BESIDE ANY SCREEN THAT USES IT.
 * The §8.4 warning is the sharpest line in the TODO — *"the predecessor rendered
 * 8 hardcoded children while working endpoints sat uncalled"* — and the platform
 * tier is where that failure would be most tempting, because every one of these
 * endpoints has been shipped, tested and audited for a unit with no screen at
 * all. Sample schools on an operator's dashboard would be the same failure with
 * a bigger blast radius.
 *
 * 🔴 F3 — SUBSCRIPTIONS, SEATS, MRR — IS ABSENT, AND ITS ABSENCE IS THE
 * DECISION. `admin-panel.md` §2 lists MRR in F1's panel table and
 * `platform-metrics.ts` refuses it in writing: OD-4 (pricing) is open, there is
 * no payment provider in the repo, and `org.prisma` says *"billing/plan/seat
 * columns are deliberately absent — a speculative column is a migration you live
 * with."* What ships instead is a read-only **enrolment** figure, labelled
 * enrolment, never priced. A `getSubscriptions` here would be a function
 * returning a number multiplied by a price nobody has agreed.
 */

/* ─────────────────────────────────────────────────────────────── F1 ── */

/** Why a school is, or is not, on the at-risk list. A reason, never a boolean. */
export type RiskVerdict =
  | 'AT_RISK'
  | 'OK'
  | 'TOO_SMALL_TO_JUDGE'
  | 'NOT_ENOUGH_HISTORY'
  | 'NOT_ACTIVE'

export type SignalCode = 'NO_SEATS' | 'NO_TEACHERS' | 'NOBODY_PLAYED' | 'LOW_ENGAGEMENT'

/**
 * 🔴 A PAIR, NEVER A PERCENTAGE — the denominator travels with the count.
 * OD-39/OD-72, and `health-score.ts` states it on the other side: *"a school
 * where two of three children played is not 67% engaged; it is two of three."*
 */
export interface HealthSignal {
  code: SignalCode
  count: number
  of: number
  /** What to do about it. A signal with no action is a number on a dashboard. */
  action: string
}

export interface SchoolHealth {
  /**
   * 🔴 A WORD, NOT A SCORE OUT OF A HUNDRED — and a word cannot be averaged,
   * which is deliberate: a mean health score is exactly the metric that hides
   * the one school that is failing.
   */
  verdict: 'OK' | 'WATCH' | 'AT_RISK'
  signals: HealthSignal[]
  enrolled: number
}

export interface SchoolMetrics {
  schoolId: string
  name: string
  status: SchoolStatus
  region: Region
  /** The seat count. Children in non-archived classes. Never a price. */
  enrolledStudents: number
  activeStudents: number
  teachers: number
  /** `null` when there are no seats — never `0`, which reads as "nobody plays". */
  engagement: number | null
  risk: RiskVerdict
  health: SchoolHealth
}

export interface PlatformOverview {
  generatedAt: string
  schools: { total: number; byStatus: Record<SchoolStatus, number> }
  /** 🔴 Labelled enrolment. There is no revenue field on this object. */
  enrolment: {
    seats: number
    /**
     * Children with no class at all. `Player` has no `schoolId`, so no school
     * can claim them and every scoped query in the product is blind to them —
     * this is the one place anybody can see them. It should be zero.
     */
    unattributedChildren: number
    rule: string
  }
  teachers: { active: number }
  engagement: {
    windowDays: number
    activeStudents: number
    /** Over ACTIVE schools only — a suspension is not a product event. */
    rate: number | null
  }
  atRisk: SchoolMetrics[]
  perSchool: SchoolMetrics[]
}

/* ─────────────────────────────────────────────────────────────── F2 ── */

export interface SchoolSummary {
  id: string
  name: string
  status: SchoolStatus
  region: Region
  activatedAt: string | null
  suspendedAt: string | null
  archivedAt: string | null
  createdAt: string
  classCount: number
  /** Active staff with `role: 'ADMIN'`. Zero is the state F2 exists to fix. */
  adminCount: number
  /**
   * 🔴 WHAT F2 MAY OFFER AS BUTTONS, AND THE SCREEN RENDERS FROM THIS RATHER
   * THAN FROM ITS OWN COPY OF THE TABLE. D4's lesson: *"the UI must not offer a
   * promote button that no-ops or throws"* — and a second transitions table in
   * the browser would disagree with `school-lifecycle.ts` the first time either
   * moved, with the disagreement looking like a missing button.
   */
  legalNextStatuses: SchoolStatus[]
}

export interface RegionChangeResult extends SchoolSummary {
  /**
   * 🔴 THE REGION CHANGE MOVES NO EXISTING `retainUntil`, AND THE RESPONSE SAYS
   * SO. `retainUntil` is stamped at grant time, never computed, so changing the
   * region changes what NEW consents are stamped with and nothing already
   * written. An operator who changed the region to "fix retention" must be told.
   */
  consentsKeepingTheirRetainUntil: number
}

export interface ProvisionedAdmin {
  userId: string
  email: string
  name: string
  schoolId: string
  /**
   * 🔴 ALWAYS `null`, AND THE FIELD IS KEPT SO THE ABSENCE IS EXPLICIT.
   *
   * It carried a generated credential on a path that no longer exists. The
   * operator types the password now, so they already hold it — echoing it back
   * would put a live school-admin login into one more response body, one more
   * proxy log and one more browser heap for nothing gained.
   */
  temporaryPassword: null
  /**
   * 🔴 ONE VALUE. There were three — `'GENERATED'`, `'OPERATOR'`, `'INVITE'` —
   * because there were three different sentences a screen had to choose between,
   * two of them arriving with a null password. There is one now: the operator
   * typed it and hands it over. The field survives as a literal so a screen
   * written against the old shape fails to compile rather than silently
   * rendering *"copy this now"* about a string that is not there.
   */
  /**
   * 🔴 TWO VALUES AGAIN — CHANGE 19 §1. It was narrowed to one literal so that a
   * screen written against the old shape would *fail to compile rather than
   * silently render the wrong sentence*, and that is exactly why widening it is
   * safe: a screen that only knows `'OPERATOR'` will not compile against
   * `'INVITE'`. *"Hand over what you typed"* and *"they have been emailed a
   * link"* are two different things to tell an operator, and now both happen.
   */
  passwordSource: 'OPERATOR' | 'INVITE'
  /**
   * 🔴 WHETHER THE EMAIL ACTUALLY WENT.
   *
   * `'NOT_ATTEMPTED'` on the password path — there was nothing to send.
   * `'SENT'` and `'FAILED'` both leave a working school with a real account and
   * a real invite; only one of them leaves a head teacher who can get in, and
   * folding them together is how an operator closes the tab believing somebody
   * has a link they do not have.
   */
  delivery: InviteDelivery
  /** When the link dies. `null` when there is no link. */
  inviteExpiresAt: string | null
  /**
   * 🔴 THE CLAIM LINK, ONLY WHEN `delivery` IS `'FAILED'` — the honest fallback
   * to a dead end. It is a working credential until used: show it once, never
   * store it, and tell the operator to share it privately. `null` otherwise.
   */
  claimUrl: string | null
  /** How many active admins this school already had. Not asked for; needed. */
  existingAdminCount: number
}

/**
 * 🔴 WHETHER THE EMAIL ACTUALLY WENT — NAMED, BECAUSE TWO PAYLOADS CARRY IT.
 *
 * It was written inline on `ProvisionedAdmin` when creation was the only thing
 * that sent one. Re-sending answers with the same three-way fact minus
 * `'NOT_ATTEMPTED'`, and a second inline copy is the one that drifts.
 */
export type InviteDelivery = 'SENT' | 'FAILED' | 'NOT_ATTEMPTED'

/**
 * What `POST /superadmin/schools` answers with when it also minted the login.
 *
 * ⚠️ `admin: null` IS A COMPLETE SUCCESS, not a partial one. A school created
 * from a signed contract before anybody has been named is a real case, and F2's
 * list sorts exactly that row to the top.
 */
export interface CreatedSchool extends SchoolSummary {
  admin: ProvisionedAdmin | null
  /**
   * 🔴 THE PLAN AND THE TRIAL, IF ONE WAS SET IN THE SAME REQUEST.
   *
   * `admin-panel.md` §3 defines onboarding as *"create school, set plan, invite
   * the school admin"*, and until now this client could only ask for two of the
   * three. `null` is a complete success for the same reason `admin: null` is.
   *
   * ⚠️ TYPED AS `SubscriptionRow` FROM `billing.ts` RATHER THAN RESTATED HERE.
   * It is the same shape the F3 screens already render, and a second declaration
   * of it is the one that eventually disagrees with the server.
   */
  subscription: SubscriptionRow | null
}

/* ─────────────────────────────────────────── F2 · one school in full ── */

/** One member of staff, as the operator's school page shows them. */
export interface SchoolStaffDetail {
  userId: string
  name: string | null
  /**
   * 🔴 PRESENT HERE AND ABSENT FROM `SchoolStaff`, AND THE DIFFERENCE IS THE
   * QUESTION EACH ANSWERS. The picker only needs enough to choose a person; this
   * page is opened by an operator on the phone to that school, and *"which
   * address does the head teacher sign in with"* is the first thing they are
   * asked. The cost is that this read is audited and the picker's is not.
   */
  email: string | null
  role: string
  isActive: boolean
  lastLoginAt: string | null
  createdAt: string
}

export interface SchoolClassDetail {
  id: string
  name: string
  grade: string | null
  section: string | null
  ageBand: string
  /** What a child types to sign in. `null` means no child in it can sign in yet. */
  code: string | null
  academicYear: string | null
  /** Non-null means archived. Archived classes are listed and marked, never dropped. */
  archivedAt: string | null
  teacherName: string | null
  /** A class can have a teacher whose `name` is null — this is the real question. */
  hasTeacher: boolean
  students: number
}

/**
 * Everything about one school, in one request.
 *
 * 🔴 ONE CALL RATHER THAN SIX, because the panel is opened by clicking a table
 * row: a page assembling itself from `/schools/:id`, `/staff`, `/features`,
 * `/audit?targetId=…` and two more would spend its first second in six separate
 * loading states.
 *
 * ⚠️ NOTHING HERE IS A CHILD. Classes carry counts, staff carry logins, and
 * there is no player, session or event in the payload — an operator who needs a
 * child's record uses X3, which is audited per child and says so.
 */
export interface SchoolDetail {
  school: SchoolSummary
  settings: {
    recordingDefault: boolean
    defaultLanguage: string
    academicYearLabel: string | null
  }
  counts: {
    classes: number
    archivedClasses: number
    students: number
    activeStudents: number
    /** The window `activeStudents` was counted over. A count with no window is a number. */
    engagementWindowDays: number
    admins: number
    teachers: number
    coordinators: number
    principals: number
    inactiveStaff: number
  }
  staff: SchoolStaffDetail[]
  classes: SchoolClassDetail[]
  features: SchoolFeature[]
  rollovers: { id: string; fromYear: string; toYear: string; completedAt: string | null }[]
  /**
   * 🔴 `SUPER_ADMIN_ACCESS` IS FILTERED OUT SERVER-SIDE. Opening this page writes
   * one, so including them would make the list read *"an operator opened this
   * page"* forty times over and bury the status change that matters. They are one
   * click away in F5, and the screen says so.
   */
  recentActivity: {
    id: string
    actorId: string | null
    actorLabel: string
    action: string
    summary: string
    createdAt: string
  }[]
}

/* ────────────────────────────────────────────────────────── F4 · X1 · X6 ── */

export interface ReleaseView {
  id: string
  version: string
  state: ContentState
  notes: string | null
  publishedAt: string | null
  retiredAt: string | null
  createdAt: string
  /** Forward-only. Again: the buttons come from the server, not from a copy. */
  legalNextStates: ContentState[]
}

export interface SchoolFeature {
  feature: string
  enabled: boolean
  note: string | null
  updatedAt: string
}

export interface MaintenanceState {
  enabled: boolean
  message: string | null
  updatedAt: string | null
}

/** F4's beta opt-in is an X1 flag — one question, one mechanism. */
export const BETA_CONTENT_FEATURE = 'content.beta'

/* ─────────────────────────────────────────────────────────────── F5 ── */

export interface AuditRow {
  id: string
  /** `null` means the actor was the system, not a user called "system". */
  actorId: string | null
  actorLabel: string
  action: AuditAction
  targetType: string
  targetId: string | null
  summary: string
  createdAt: string

  /* ── resolved at read time, stored nowhere. See `audit-query.ts`. ── */

  /**
   * 🔴 THE ANSWER TO "WHO", IN WORDS. The stored `actorLabel` is an id, on
   * purpose — a name in that column would put a staff member's personal data
   * into a table retained beyond the ordinary window. So the server joins the
   * name on the way out and stores nothing: this column is what a human reads,
   * `actorId` is what a filter uses, and neither is written anywhere new.
   *
   * ⚠️ `null` FOR THE SYSTEM AND FOR A DELETED ACTOR, and those are different
   * facts the screen tells apart by looking at `actorId` — null there means the
   * system, non-null with no name means the person is gone. The log surviving
   * its actors is the property `actorLabel` was denormalised for.
   */
  actorName: string | null
  actorEmail: string | null
  /** Their role TODAY. A role change since the row was written shows up here. */
  actorRole: string | null
  /**
   * What the target is called — a school's name, a person's, a class with its
   * school, a child with their roll number. `null` when the type is not one the
   * server resolves, or the row it named is gone; the screen shows the id then,
   * which is still the fact the log recorded.
   */
  targetLabel: string | null
  /**
   * 🔴 THE OPERATOR BEHIND AN IMPERSONATED ACTION. `AuditLog.impersonatorId` has
   * existed since impersonation shipped and nothing read it, so a row written by
   * an operator acting as a head teacher was indistinguishable from one the head
   * teacher wrote — which is the exact confusion `admin-panel.md` §8 warns about.
   */
  impersonatorId: string | null
  impersonatorName: string | null
  /** An operator's duty today (OWNER, SALES…); null for everyone else. */
  actorDuty: string | null
  /** P2-05 — a school-staff actor's school ("Kavya Rao · Vidya Niketan · Admin"). */
  actorSchoolName: string | null
  /** The school the row concerns. Null on rows written before the column existed. */
  schoolId: string | null
  schoolName: string | null
  /** The PII-free second line — counts, from→to, paise. Flat scalars only. */
  detail: Record<string, string | number | boolean | null> | null
}

export interface AuditPage {
  rows: AuditRow[]
  /** `null` when this page is the last one. */
  nextCursor: string | null
  /** Echoed, so a client sees the bound it was GIVEN rather than asked for. */
  limit: number
  /** P1-47 — rows matching these filters, exact up to 10,000. */
  total: number
  /** More than `total` — the screen says "10,000+". */
  totalCapped: boolean
}

export interface AuditFilters {
  action?: AuditAction
  actorId?: string
  targetType?: string
  targetId?: string
  /** IST day (`2026-08-01`), inclusive of the whole day — see `audit-query.ts`. */
  from?: string
  to?: string
  /** Server search: a person, a school, words of an action, or summary text. */
  q?: string
  cursor?: string
  limit?: number
}

/**
 * ✅ P1-47 — EVERY AUDITED ACTION IS FILTERABLE.
 *
 * This list used to mirror the server's hand-kept DTO list, which lacked nine
 * enum values (impersonation, invites, suspensions…) and then the 38 the
 * console added — offering one would have been a 400. The server now accepts
 * the whole enum (`dto/audit-query.dto.ts` derives it and a spec reads
 * `audit.prisma`), so this is the whole enum too, read from the generated
 * mirror rather than typed out.
 */
export const FILTERABLE_AUDIT_ACTIONS: readonly AuditAction[] = Object.values(AUDIT_ACTION_VALUES)

export type FilterableAuditAction = AuditAction

/* ──────────────────────────────────────────────────────── operators ── */

export interface OperatorSummary {
  userId: string
  email: string | null
  name: string | null
  isActive: boolean
  /** The row exists but nobody has claimed the invitation and set a password. */
  awaitingClaim: boolean
  createdAt: string
  /**
   * 🔴 FALSE FOR THE CALLER AND FOR THE LAST ACTIVE OPERATOR. The server refuses
   * both regardless — this is what lets the UI avoid rendering a button that
   * would lock the platform out of itself.
   */
  mayDeactivate: boolean
}

export interface CreatedOperator {
  userId: string
  email: string
  name: string
  /*
   * ⛔ `inviteToken` AND `expiresAt` STOOD HERE. An operator account is created
   * with a password the caller typed and is ready to sign in — see
   * `createOperator` — so there is no link to show and no credential to return.
   */
}

/* ─────────────────────────────────────────────────────── X5 · rollout ── */

/** One school a rollout rule considered. */
export interface RolloutCandidate {
  schoolId: string
  name: string
  status: SchoolStatus
  region: Region
  enrolledStudents: number
  alreadyTargeted: boolean
}

export interface RolloutRule {
  regions?: readonly Region[]
  minStudents?: number
  maxStudents?: number
  limit?: number
}

export interface RolloutPlan {
  /** Schools that would gain the flag. */
  target: RolloutCandidate[]
  /** Matched the rule and already have it — reported, never re-flagged. */
  alreadyTargeted: RolloutCandidate[]
  /**
   * 🔴 CUT BY `limit`, AND NAMED RATHER THAN SILENTLY DROPPED. A plan showing
   * ten of forty without saying so reads as "forty matched" to whoever approves
   * it — *"no silent caps"*.
   */
  heldBack: RolloutCandidate[]
  /** Every school considered, so the denominator is on screen. */
  considered: number
}

/* ────────────────────────────────────────────────────── X4 · support ── */

/** One staff member an operator may act as. See `schools.service.ts` `staff()`. */
export interface SchoolStaff {
  userId: string
  role: 'ADMIN' | 'TEACHER' | 'COORDINATOR' | 'PRINCIPAL'
  label: string
}

export interface ImpersonationGrant {
  /**
   * 🔴 AN ACCESS TOKEN AND NO REFRESH TOKEN, AND THE MISSING ONE IS THE TIME-BOX.
   *
   * `impersonation.service.ts`: a refresh token would let the session renew
   * itself for thirty days, which is a permanent second identity. The absence is
   * the feature — a client that "helpfully" fell back to the operator's own
   * refresh token on expiry would undo it, so nothing here stores this beside
   * one.
   */
  accessToken: string
  actingAs: { userId: string; role: string; label: string }
  expiresInSeconds: number
}

/* ────────────────────────────────────────────── F1 · the growth curve ── */

/**
 * One day on the platform's growth curve.
 *
 * 🔴 `total` AND `added` TRAVEL TOGETHER, so the card can answer both questions
 * from one read: *"how big is this"* (the line) and *"is it still moving"* (the
 * caption). Two charts for that would be one chart too many on a dashboard.
 */
export interface GrowthPoint {
  /** `YYYY-MM-DD`, UTC — the bucket, not a rendered date. The screen formats it. */
  day: string
  students: number
  teachers: number
  parents: number
  added: { students: number; teachers: number; parents: number }
}

export interface GrowthSeries {
  generatedAt: string
  /** 🔴 The window, beside the numbers — OD-39/OD-72 applied to time. */
  windowDays: number
  /**
   * 🔴 THE COUNTING RULE, IN WORDS, FROM THE SERVER. The curve is built from
   * records that survive today, so it is not an audit of what each day looked
   * like — and a reader who assumes otherwise is reading something that was
   * never measured. The screen prints this rather than restating it, because a
   * second copy of the sentence is a second copy to drift.
   */
  basis: string
  points: GrowthPoint[]
}

/* ──────────────────────────────────────────── F1 · the activity feed ── */

/**
 * One thing that happened on the platform.
 *
 * 🔴 NOT FROM THE AUDIT LOG. That read writes a `SUPER_ADMIN_ACCESS` row every
 * time — deliberately, because its rows name children — so a card on the morning
 * dashboard cannot be sourced from it without burying the log in the act of
 * reading it. These events come from schools, subscriptions, coupons and demo
 * requests, and none of them names a person.
 */
export interface ActivityEvent {
  /** `${kind}:${row id}` — stable across refetches, so a list keys on it rather than on an index. */
  id: string
  kind:
    | 'SCHOOL_ONBOARDED'
    | 'SUBSCRIPTION_STARTED'
    | 'SUBSCRIPTION_CHANGED'
    | 'COUPON_CREATED'
    | 'REQUEST_RECEIVED'
  at: string
  summary: string
  /** The school to open, or `null` — an F6 request has no school record yet. */
  schoolId: string | null
}

export interface ActivityFeed {
  generatedAt: string
  events: ActivityEvent[]
}

/* ──────────────────────────────────────────────────────── the machinery ── */

/** The closed list `jobs.controller.ts` checks a path parameter against. */
export const JOB_NAMES = ['notification-outbox', 'weekly-digest', 'data-retention'] as const
export type JobName = (typeof JOB_NAMES)[number]

export interface JobRunResult {
  job: JobName
  status: string
  processed: number
  failed: number
  durationMs: number
  /** 🔴 Whether anything was actually deleted. `RETENTION_DRY_RUN` still governs it. */
  dryRun: boolean
  note?: string
}

/** A pair and a verdict, never a bare percentage (OD-39/OD-72). */
export interface TrackingRate {
  abandonedTracking: number
  started: number
  /** `null` below the sample floor — a rate nobody should read. */
  rate: number | null
  verdict: 'HEALTHY' | 'INVESTIGATE' | 'NOT_WORKING' | 'TOO_FEW_ROUNDS'
}

export interface ProductHealth {
  windowDays: number
  tracking: {
    overall: TrackingRate
    byBand: Record<string, TrackingRate>
    byGame: Record<string, TrackingRate>
    byDeviceClass: Record<string, TrackingRate>
    /** Segments worse than the overall — where to look first. */
    worseThanOverall: string[]
  }
  fps: { p10: number | null; median: number | null; samples: number; belowFloor: boolean }
  /** The counter-metric: a rise means children are routing around a failing camera. */
  fallback: { usedFallback: number; completed: number; rate: number | null }
}

/* ───────────────────────────────────────────────────────────── calls ── */

const get = <T>(path: string, auth: StaffAuth): Promise<T> =>
  request<T>(path, { context: 'user', token: auth.token, signal: auth.signal })

const post = <T>(path: string, body: unknown, auth: StaffAuth): Promise<T> =>
  request<T>(path, { method: 'POST', body, context: 'user', token: auth.token, signal: auth.signal })

/**
 * F1. Read-only — there is no write on this path and there must not be one.
 *
 * ⚠️ `window` NARROWS THE ENGAGEMENT WINDOW AND NOTHING ELSE. Active students,
 * the engagement rate, every school's health verdict and the at-risk list move
 * with it; the school roll, seat count and teacher headcount are what exists
 * today and hold still. `engagement.windowDays` in the response names the span
 * that was actually measured, so a screen never has to assume.
 */
export const getPlatformOverview = (
  auth: StaffAuth = {},
  window?: DateWindow,
): Promise<PlatformOverview> => get(`/superadmin/platform${windowQuery(window)}`, auth)

/* ── F2 ── */

export const getSchools = (auth: StaffAuth = {}): Promise<SchoolSummary[]> =>
  get('/superadmin/schools', auth)

export interface CreateSchoolBody {
  name: string
  /**
   * 🔴 REQUIRED, EVEN THOUGH THE COLUMN HAS A DEFAULT. X2 hangs a child's
   * deletion date off the region, and it is the one fact about a school that
   * cannot be inferred later — a child has no PII from which a jurisdiction
   * could be derived, by design (ADR-004). Leaning on the default would make
   * every school's retention jurisdiction a decision taken by omission.
   */
  region: Region
  /**
   * 🔴 THE FIRST LOGIN, MINTED IN THE SAME REQUEST — OPTIONAL, AND BOTH STATES
   * ARE REAL.
   *
   * Onboarding a school and giving somebody a way into it are one decision on
   * the phone call where both happen; splitting them across two requests is how
   * `adminCount: 0` becomes permanent, because the browser closes in between.
   * Omit it and nothing changes: the school arrives with no admin, and F2's list
   * sorts that row to the top.
   */
  /**
   * 🔴 A DISCRIMINATED UNION, AND THE DISCRIMINATOR IS NEVER OPTIONAL — CHANGE
   * 19 §1. The two ways a school admin gets in are now genuinely different
   * writes, and the difference must be typed rather than inferred from whether a
   * password happens to be present:
   *
   *   - `EMAIL` — nobody chooses a password. The account is created inactive
   *     with no hash, and a single-use link is emailed. **Prefer this**: it is
   *     the only path where no human ever handles a working credential.
   *   - `PASSWORD` — the operator types one and hands it over, as before. It
   *     stays because a school with no working mailbox is real, and because a
   *     mail outage must not stop an onboarding.
   */
  admin?:
    | { delivery: 'EMAIL'; email: string; name: string }
    | { delivery: 'PASSWORD'; email: string; name: string; password: string }
  /**
   * 🔴 THE PLAN AND THE FREE TRIAL, IN THE SAME REQUEST — §3's *"set plan"*.
   *
   * It used to require a second visit to F3, found by searching a different
   * table for the school just created; the gap between the two requests is where
   * a school with an admin and no plan comes from.
   *
   * ⚠️ `trialEndsAt` IS AN ABSOLUTE ISO DATE AND IT IS REQUIRED WHEN THIS BLOCK
   * IS SENT AT ALL. A trial with no end is not a trial. The form does the
   * arithmetic from a number of days and shows the date, so the operator agrees
   * to the date rather than to a clock on a server.
   *
   * ⚠️ OMIT THE WHOLE BLOCK for a school whose commercial terms are not settled.
   * There is deliberately no way to say *"a plan, but no trial"* here — that is a
   * school which is already paying, which is F3's screen and F3's route.
   */
  subscription?: {
    planId: string
    trialEndsAt: string
    seatLimit?: number
    note?: string | null
  }
}

/*
 * ✅ `DeliveryMode` IS BACK, AS `admin.delivery` ABOVE. It was deleted rather
 * than narrowed *"so every screen that used to branch on it has to be looked at
 * rather than quietly compiling"* — and that is what makes re-adding it safe:
 * the same rule now forces every screen to be looked at again.
 */

export const createSchool = (body: CreateSchoolBody, auth: StaffAuth = {}): Promise<CreatedSchool> =>
  post('/superadmin/schools', body, auth)

/**
 * Everything about one school.
 *
 * ⚠️ THIS READ IS AUDITED AND THE LIST IS NOT. It carries staff login
 * addresses, so the server writes a `SUPER_ADMIN_ACCESS` row naming the school
 * every time it is called. Calling it on a hover, a poll or a prefetch would
 * write a row an incident then has to rule out — it belongs behind a deliberate
 * "open this school".
 */
export const getSchoolDetail = (schoolId: string, auth: StaffAuth = {}): Promise<SchoolDetail> =>
  get(`/superadmin/schools/${encodeURIComponent(schoolId)}/detail`, auth)

/**
 * 🔴 ONE ROUTE FOR THE WHOLE LIFECYCLE, NAMING A TARGET STATE — never
 * `/activate`, `/suspend`, `/archive`. Four verbs would put the legal-transitions
 * table in the URL space, where it could disagree with `school-lifecycle.ts`.
 *
 * ⚠️ A NO-OP IS A SUCCESS, NOT AN ERROR. Asking for the state a school is
 * already in returns the school unchanged and writes no audit row — an operator
 * double-clicking suspend on a slow connection must not leave two
 * `SCHOOL_STATUS_CHANGE` entries for one decision, in the table that is the
 * first artefact requested in an incident.
 */
/*
 * ✅ P1-09 / D15 — the server now needs `customers.lifecycle` and a REASON
 * (3–300 characters) that lands in the audit row. `reason` is optional in this
 * signature only so screens built before it keep compiling; the server answers
 * 400 until they send one.
 */
export const transitionSchool = (
  schoolId: string,
  to: SchoolStatus,
  auth: StaffAuth = {},
  reason?: string,
): Promise<SchoolSummary> =>
  patch(
    `/superadmin/schools/${encodeURIComponent(schoolId)}/status`,
    reason === undefined ? { to } : { to, reason: reason.trim() },
    auth,
  )

export const changeSchoolRegion = (
  schoolId: string,
  region: Region,
  auth: StaffAuth = {},
): Promise<RegionChangeResult> =>
  patch(`/superadmin/schools/${encodeURIComponent(schoolId)}/region`, { region }, auth)

/**
 * 🔴 SEND THE SET-UP LINK AGAIN — THE ACTION `delivery: 'FAILED'` NAMED AND
 * NOBODY COULD PERFORM.
 *
 * A failed send leaves a school, an admin account and an unclaimed invite all
 * real; only the delivery failed. Before this route the operator's only recovery
 * was to provision a second admin, which is one school with two accounts and one
 * working mailbox.
 *
 * ⚠️ IT ANSWERS WITH WHETHER THE SEND WORKED, AND WITH THE LINK ONLY WHEN IT
 * DID NOT. While the mail works no human holds a working credential; when it
 * fails, `claimUrl` is the fallback (see `ProvisionedAdmin.claimUrl`).
 *
 * ⚠️ REFUSED, WITH A 400, FOR AN ADMIN WHO HAS ALREADY SET A PASSWORD. That is
 * a password reset — a different door — and issuing a fresh link for a live
 * account would be the ability to overwrite a working credential.
 */
export interface ResentInvite {
  userId: string
  delivery: InviteDelivery
  inviteExpiresAt: string
  /** The new link, only when `delivery` is `'FAILED'`. */
  claimUrl: string | null
}

export const resendAdminInvite = (
  schoolId: string,
  userId: string,
  auth: StaffAuth = {},
): Promise<ResentInvite> =>
  post(
    `/superadmin/schools/${encodeURIComponent(schoolId)}/admins/${encodeURIComponent(userId)}/invite`,
    {},
    auth,
  )

export interface ProvisionAdminBody {
  email: string
  name: string
  /**
   * 🔴 THE OPERATOR'S OWN PASSWORD — REQUIRED, AND THE ONLY WAY IN.
   *
   * This field did not exist at all, then existed and was optional beside an
   * invitation link. Both of those are gone. The reason is not that a chosen
   * password became safe — it is still one the operator knows and can read back
   * out of their own ticketing system — but that **there is no email sender in
   * this product**, so the invitation link was read out down the same phone call
   * the password would have been, and the generated alternative was a
   * sixteen-symbol string that fails at exactly that moment.
   *
   * The named failure is closed rather than waived: `password-policy.ts` on the
   * server (and `passwordProblem` in `platformModel.ts` here, so the refusal is
   * a sentence rather than a 400) reject `Welcome@123`, every leet decoration of
   * it, and anything derived from the school's own name or this address.
   */
  password: string
  /**
   * ⚠️ ADVISORY, AND THE SERVER RE-CHECKS AGAINST THE ROW. The password rule
   * needs the school's name to refuse `LittleStars@2026`, and the request only
   * carries an id in its path — so the client sends the name it is displaying.
   * A caller that omitted or falsified it gains nothing.
   */
  schoolName?: string
}

export const provisionSchoolAdmin = (
  schoolId: string,
  body: ProvisionAdminBody,
  auth: StaffAuth = {},
): Promise<ProvisionedAdmin> =>
  post(`/superadmin/schools/${encodeURIComponent(schoolId)}/admins`, body, auth)

/* ── operators ── */

export const getOperators = (auth: StaffAuth = {}): Promise<OperatorSummary[]> =>
  get('/superadmin/operators', auth)

/**
 * 🔴 THE RECOVERY PATH `seed.ts` NAMES AND THE PRODUCT DID NOT HAVE.
 *
 * The account is created **active, with the password the caller supplies**, and
 * that is a reversal: it used to be created deactivated and password-less behind
 * an invitation link. There is no mail sender in this product, so the link was
 * read out down the same phone call — buying none of its safety while stopping
 * the colleague on the other end signing in before the call ended.
 * `operator.dto.ts` on the server carries the argument in full.
 *
 * ⚠️ STILL NO `role` FIELD. This route creates a SUPER_ADMIN and nothing else,
 * and `.strict()` makes sending one a 400 rather than a silently ignored key.
 *
 * ⚠️ THE PASSWORD GOES THROUGH THE SAME POLICY AS A SCHOOL ADMIN'S —
 * `passwordProblem` here, `password-policy.ts` there — so the refusal is a
 * sentence on the form rather than a bodyless 400.
 */
export const createOperator = (
  body: { email: string; name: string; password: string },
  auth: StaffAuth = {},
): Promise<CreatedOperator> => post('/superadmin/operators', body, auth)

/**
 * ⚠️ `POST …/deactivate`, NOT `DELETE`. There is no hard delete for an operator:
 * the audit rows they wrote name them as the actor, and deleting the row makes
 * every one of those entries point at nothing — on the table an incident is
 * reconstructed from.
 */
export const deactivateOperator = (
  userId: string,
  auth: StaffAuth = {},
): Promise<{ revokedSessions: number }> =>
  post(`/superadmin/operators/${encodeURIComponent(userId)}/deactivate`, {}, auth)

/* ── X5 ── */

/**
 * *"Who would this affect?"* — asked before anybody answers it by doing it.
 *
 * 🔴 A `POST` THAT WRITES NOTHING, and the verb is the lesser evil: the rule is
 * a nested object (`regions` is a list), and a GET would put repeated
 * `?regions=` parameters in a query string that `.strict()` cannot describe.
 *
 * 🔴 THERE IS NO `applyRollout` IN THIS FILE AND THERE MUST NOT BE ONE. Granting
 * is `setSchoolFeature` — one call per school, each audited under its own
 * `FEATURE_FLAG_CHANGE` row naming one school. A bulk-apply would turn a list an
 * operator was supposed to read into a single button, which is precisely the
 * separation `rollout.ts` exists to enforce.
 */
export const planRollout = (rule: RolloutRule, auth: StaffAuth = {}): Promise<RolloutPlan> =>
  post('/superadmin/rollout/plan', rule, auth)

/* ── X4 ── */

/** The target picker. A read on the schools surface, not on `/auth` — see the controller. */
export const getSchoolStaff = (schoolId: string, auth: StaffAuth = {}): Promise<SchoolStaff[]> =>
  get(`/superadmin/schools/${encodeURIComponent(schoolId)}/staff`, auth)

/**
 * 🔴 THE REASON IS A REQUIRED ARGUMENT, NOT AN OPTIONAL ONE, AND THE TYPE IS
 * WHERE THAT IS ENFORCED ON THIS SIDE.
 *
 * `impersonateSchema` demands ≥8 characters and the service refuses an empty
 * one; making it optional here would push the whole question to a runtime 400
 * on the one action whose entire justification is that somebody said why.
 */
export const startImpersonation = (
  body: { userId: string; reason: string },
  auth: StaffAuth = {},
): Promise<ImpersonationGrant> => post('/auth/impersonate', body, auth)

/**
 * ⚠️ IT CANNOT REVOKE THE TOKEN AND MUST NOT CLAIM TO. Access tokens are
 * verified by signature with no store lookup (§8.1), so the session ends when
 * the token expires; this records the intent, which is what an investigator
 * reads. The UI's job is to stop USING the token — which is a client-side
 * discard, not something this call achieves.
 */
export const endImpersonation = (auth: StaffAuth = {}): Promise<{ ended: true }> =>
  post('/auth/impersonate/end', {}, auth)

/* ── X3 ── */


/* ── the machinery ── */

/**
 * F1's growth chart.
 *
 * 🔴 THE CARD DREW THIS FROM LITERALS UNTIL THIS ROUTE EXISTED — three fixed SVG
 * paths, eighteen fixed points and five fixed axis labels reading "May 20 – Jun
 * 17", rendered identically on an empty platform and a full one.
 *
 * ⚠️ `days` IS BOUNDED SERVER-SIDE (7–365) AND SENT ONLY WHEN ASKED FOR. The
 * schema is `.strict()`, so an empty value is a 400 rather than an ignored
 * parameter — `auditQueryString`'s rule.
 */
/**
 * ⚠️ `window` WINS OVER `days` SERVER-SIDE, and `windowDays` in the response
 * reports whatever was drawn. `days` can only ever mean *"the last N"*; a period
 * that ended in the past is exactly what two date pickers promise.
 */
export const getPlatformGrowth = (
  days?: number,
  auth: StaffAuth = {},
  window?: DateWindow,
): Promise<GrowthSeries> => {
  const daysPart = days === undefined ? '' : `?days=${String(days)}`
  const windowPart = windowQuery(window, daysPart === '' ? '?' : '&')
  return get(`/superadmin/growth${daysPart}${windowPart}`, auth)
}

/**
 * F1's activity feed. `limit` is bounded 1–50 server-side; the card asks for six.
 *
 * ⚠️ WITH A `window` THIS STOPS MEANING "the latest few things" AND STARTS
 * MEANING "what happened in this period". Under a date filter that is the only
 * honest reading: a card that ignored the filter would be the one panel on the
 * screen quietly answering a different question from every tile above it.
 */
export const getPlatformActivity = (
  limit?: number,
  auth: StaffAuth = {},
  window?: DateWindow,
): Promise<ActivityFeed> => {
  const limitPart = limit === undefined ? '' : `?limit=${String(limit)}`
  const windowPart = windowQuery(window, limitPart === '' ? '?' : '&')
  return get(`/superadmin/activity${limitPart}${windowPart}`, auth)
}

/**
 * ⚠️ THIS WORKS EVEN WHEN `JOBS_ENABLED=false`, WHICH IS THE POINT — on a
 * machine with the schedules off, it is how a job is exercised at all. It is not
 * a way round the safety flags: `RETENTION_DRY_RUN` still governs whether
 * anything is deleted, which is why `JobRunResult.dryRun` is on screen.
 */
export const runJob = (name: JobName, auth: StaffAuth = {}): Promise<JobRunResult> =>
  post(`/jobs/${encodeURIComponent(name)}/run`, {}, auth)

/** *"The one to open first each morning during a pilot, before the service one."* */
export const getProductHealth = (days: number, auth: StaffAuth = {}): Promise<ProductHealth> =>
  get(`/telemetry/health?days=${String(days)}`, auth)

/* ── F4 ── */

export const getReleases = (auth: StaffAuth = {}): Promise<ReleaseView[]> =>
  get('/platform/content', auth)

/** Always a draft. A create call that could name PUBLISHED skips review by typing. */
export const createRelease = (
  body: { version: string; notes?: string },
  auth: StaffAuth = {},
): Promise<ReleaseView> => post('/platform/content', body, auth)

export const transitionRelease = (
  id: string,
  to: ContentState,
  auth: StaffAuth = {},
): Promise<ReleaseView> =>
  patch(`/platform/content/${encodeURIComponent(id)}/state`, { to }, auth)

/* ── X1 ── */

export const getSchoolFeatures = (schoolId: string, auth: StaffAuth = {}): Promise<SchoolFeature[]> =>
  get(`/platform/schools/${encodeURIComponent(schoolId)}/features`, auth)

/** Upsert, never create-or-delete: turning a flag off must survive a bulk enrolment. */
export const setSchoolFeature = (
  schoolId: string,
  body: { feature: string; enabled: boolean; note?: string },
  auth: StaffAuth = {},
): Promise<{ feature: string; enabled: boolean }> =>
  post(`/platform/schools/${encodeURIComponent(schoolId)}/features`, body, auth)

/* ── X6 ── */

export const getMaintenance = (auth: StaffAuth = {}): Promise<MaintenanceState> =>
  get('/platform/maintenance', auth)

export const setMaintenance = (
  body: { enabled: boolean; message?: string },
  auth: StaffAuth = {},
): Promise<MaintenanceState> => post('/platform/maintenance', body, auth)

/* ── F5 ── */

/**
 * 🔴 THE QUERY IS BUILT FROM A TYPED OBJECT, AND EMPTY VALUES ARE DROPPED RATHER
 * THAN SENT BLANK.
 *
 * `auditQuerySchema` is `.strict()` with `min(1)` on the string filters, so
 * `?targetType=` is a **400**, not an ignored parameter. A form whose cleared
 * input sends an empty string would therefore break the page the moment somebody
 * typed into a filter and deleted it again — during an incident, on the one
 * table people consult precisely because they suspect something happened.
 */
export function auditQueryString(filters: AuditFilters): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null) continue
    const text = String(value)
    if (text.length === 0) continue
    params.set(key, text)
  }
  const query = params.toString()
  return query.length > 0 ? `?${query}` : ''
}

export const getAuditPage = (filters: AuditFilters, auth: StaffAuth = {}): Promise<AuditPage> =>
  get(`/audit${auditQueryString(filters)}`, auth)

/**
 * X9 — the same read as a file.
 *
 * 🔴 IT FETCHES AND BUILDS THE BLOB RATHER THAN LINKING TO THE PATH. A plain
 * `<a href>` carries cookies and this API uses bearer tokens, so the link would
 * download a 401 page named `kalqy-audit-log.csv` — `school.ts` records the same
 * constraint for the coverage export.
 *
 * ⚠️ THIS IS THE RESPONSE THAT LEAVES THE BUILDING, and the server treats it as
 * such: every matching row (streamed, bounded at 250,000), ids only, audited as
 * `DATA_EXPORT` before the first byte (P1-47). `cursor` and `limit` are page
 * controls and are not sent.
 */
export function auditCsv(filters: AuditFilters, auth: StaffAuth = {}): Promise<string> {
  const rest: AuditFilters = { ...filters }
  delete rest.cursor
  delete rest.limit
  return request<string>(`/audit.csv${auditQueryString(rest)}`, {
    context: 'user',
    token: auth.token,
    signal: auth.signal,
    // The body is `text/csv`. Parsed as JSON it would throw and be classified
    // retryable — a download that succeeded, retried forever. See `client.ts`.
    expect: 'text',
  })
}

/**
 * 🔴 THE THREE LIFECYCLE WRITES ARE PATCHes, AND `client.ts` LEARNED THE VERB
 * RATHER THAN THIS FILE CASTING AROUND IT.
 *
 * `schools/:id/status`, `schools/:id/region` and `content/:id/state` each name a
 * target state on a resource that already exists. `school.ts` met the neighbouring
 * problem — it wanted PATCH semantics and sent a POST, because the route was a
 * POST — but here the routes genuinely are PATCH, so there is nothing to send a
 * POST to. See the note on `HttpMethod`.
 */
function patch<T>(path: string, body: unknown, auth: StaffAuth): Promise<T> {
  return request<T>(path, {
    method: 'PATCH',
    body,
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })
}

/* ────────────────────────────────────────── every child on the platform ── */

/**
 * One child, as the operator's tier sees them.
 *
 * 🔴 IT IS NOT `StudentRow` FROM `classes.ts`, AND THE TWO DIFFERENCES ARE BOTH
 * DELIBERATE. This one carries the school (that tier's rows cannot — every child
 * there is already in the reader's own school) and it carries play figures; it
 * does NOT carry a parent's phone number, masked or otherwise. A school admin
 * needs to ring the family. An operator does not, and every field on this
 * response crosses school boundaries.
 *
 * ⚠️ EVERY NUMBER IS A COUNT OF ROWS, NEVER A RATE. OD-39/OD-72 — and a child's
 * accuracy in particular is a pair (`correct`, `wrong`) precisely so a screen
 * cannot render "34%" against a child who has answered three questions.
 */
export interface PlatformChildRow {
  playerId: string
  name: string
  rollNumber: string | null
  ageBand: string
  /** 🔴 Null means no class, therefore no school — F1's `unattributedChildren`, by name. */
  classId: string | null
  className: string | null
  grade: string | null
  section: string | null
  schoolId: string | null
  schoolName: string | null
  schoolStatus: SchoolStatus | null
  region: Region | null
  parentName: string | null
  sessions: number
  /** Levels finished. Never levels attempted. */
  rounds: number
  correct: number
  wrong: number
  /** Distinct games with at least one event. Never "games available". */
  gamesPlayed: number
  firstPlayedAt: string | null
  lastPlayedAt: string | null
  /**
   * 🔴 ADR-030 — SITTINGS WHOSE EVENTS WERE NEVER WRITTEN. Such a session
   * contributes a zero to every figure above it, so without this column a child
   * whose parent declined recording looks identical to a child who sat down and
   * did nothing. Only one of those is a finding.
   */
  unrecordedSessions: number
}

export interface PlatformChildrenPage {
  rows: PlatformChildRow[]
  /** Every child the query could see, before the cap. */
  total: number
  /** 🔴 Named, never silent — a page that stopped at the cap without saying so reads as a total. */
  truncated: boolean
  limit: number
}

/** One sitting. */
export interface PlatformChildSession {
  id: string
  startedAt: string
  /** Null while a session is open, or when nothing closed it (OD-37). */
  endedAt: string | null
  /**
   * 🔴 `null`, NEVER `0`, FOR A SESSION NOTHING CLOSED. Sessions are closed by
   * the device; a laptop that lost power mid-round never closes one, so "no end
   * time" is common and does not mean "lasted no time". A zero would tell an
   * operator a child sat down and left.
   */
  minutes: number | null
  recorded: boolean
  events: number
  rounds: number
  className: string | null
  contentVersion: string | null
}

export interface PlatformChildDetail {
  child: PlatformChildRow
  /** The same five-game fold every other tier reads, scoped to one child. */
  games: GameStat[]
  summary: GameSummary
  sessions: PlatformChildSession[]
  sessionsTotal: number
}

/**
 * `GET /superadmin/children`.
 *
 * 🔴 AUDITED, AND IT WRITES ONE ROW NAMING NO CHILD. The list is unscoped by
 * design — that is what SUPER_ADMIN means — and `ScopeService` states the price:
 * *"access nobody can reconstruct later is indistinguishable from a breach."*
 *
 * ⚠️ NO SEARCH PARAMETER, DELIBERATELY. The screen filters this one response in
 * the browser. A `?q=` would write an audited read per keystroke whose summary
 * either carried a child's name into a long-retained table or was
 * indistinguishable from forty others.
 */
export const getPlatformChildren = (auth: StaffAuth = {}): Promise<PlatformChildrenPage> =>
  get('/superadmin/children', auth)

/**
 * `GET /superadmin/children/:playerId` — when, for how long, and how much.
 *
 * ⚠️ THIS ONE'S AUDIT ROW NAMES THE CHILD, unlike the list. Reading a roster and
 * opening a record are different acts, and the log says which.
 */
export const getPlatformChild = (
  playerId: string,
  auth: StaffAuth = {},
): Promise<PlatformChildDetail> =>
  get(`/superadmin/children/${encodeURIComponent(playerId)}`, auth)

/* ──────────────────────────────────────────────── F4b · release notices ── */

/**
 * What an operator wrote about a release, and who it was for.
 *
 * 🔴 IT IS NOT A BROADCAST. Nothing is sent — no outbox, no template, no
 * recipient row, no phone number touched. It is a strip at the top of the
 * dashboards of people with these roles, the next time they open one.
 * `template-gallery.ts` refuses a send console and every one of its reasons
 * still holds.
 */
export interface NoticeView {
  id: string
  releaseId: string | null
  version: string
  title: string
  body: string
  /** 🔴 At least one. An empty audience is a write that reaches nobody, and the server refuses it. */
  roles: string[]
  authorLabel: string
  /** `null` is written-but-showing-to-nobody. Withdrawing sets it back to null. */
  publishedAt: string | null
  createdAt: string
}

/** The reader's half: the same row with the operator-only fields dropped. */
export interface ReaderNotice {
  id: string
  version: string
  title: string
  body: string
  publishedAt: string
}

export const getNotices = (auth: StaffAuth = {}): Promise<NoticeView[]> =>
  get('/platform/notices', auth)

export const createNotice = (
  releaseId: string,
  body: { title: string; body: string; roles: readonly string[]; publish?: boolean },
  auth: StaffAuth = {},
): Promise<NoticeView> =>
  post(`/platform/notices/${encodeURIComponent(releaseId)}`, body, auth)

/**
 * ⚠️ `POST …/withdraw`, NOT `DELETE`, and it clears a date rather than removing
 * a row. What was said was said — somebody has already read it — and a delete
 * would make the audit row announcing it point at nothing.
 */
export const withdrawNotice = (id: string, auth: StaffAuth = {}): Promise<NoticeView> =>
  post(`/platform/notices/${encodeURIComponent(id)}/withdraw`, {}, auth)

/**
 * `GET /notices/mine` — what the signed-in reader should be shown.
 *
 * 🔴 EVERY TIER CALLS THIS, NOT JUST THE PLATFORM ONE, which is why it is the
 * one function in this module that a teacher's browser issues. The role comes
 * from the token and from nowhere else: a `?role=` would hand a parent whatever
 * an operator wrote for teachers.
 *
 * ⚠️ IT LIVES IN THIS MODULE ANYWAY, beside the write that produces it. A second
 * file for one GET would separate the two halves of one feature, and the role
 * list on one is what the other has to agree with.
 */
export const getMyNotices = (auth: StaffAuth = {}): Promise<ReaderNotice[]> =>
  get('/notices/mine', auth)

/* ────────────────────────────────────── change 19 §4 · platform broadcasts ── */

/**
 * A message an operator addressed to chosen roles at chosen schools.
 *
 * 🔴 IT IS NOT `NoticeView` AND NOT AN `Announcement`. A notice says what a
 * VERSION changed — change 19 §5 removed that from schools entirely — and an
 * announcement is a school writing to its own families, on a route that bars
 * SUPER_ADMIN by design. This is the platform-shaped object neither of them was.
 *
 * ⚠️ NOTHING IS SENT. No outbox, no template, no phone number touched: it is a
 * strip at the top of a dashboard the next time somebody opens one. §14's
 * WhatsApp reminders are a separate feature, and they go through the outbox
 * precisely so the opt-in, quiet-hours and linkage checks are applied.
 */
export interface BroadcastView {
  id: string
  title: string
  body: string
  /** 🔴 At least one. An empty audience is refused — it would reach nobody. */
  roles: string[]
  /** 🔴 **Empty means every school** — the opposite of what an empty `roles` means. */
  schoolIds: string[]
  /** Resolved server-side. An id whose school is gone renders as the id. */
  schoolNames: string[]
  authorLabel: string
  /** `null` is written-but-showing-to-nobody. Withdrawing sets it back to null. */
  publishedAt: string | null
  createdAt: string
}

/**
 * The reader's half.
 *
 * 🔴 THE AUDIENCE IS ABSENT AND MUST STAY ABSENT. `roles` and `schoolIds`
 * describe other people; a parent learning a message went to "every school
 * except yours" has been told something about the platform's customer list.
 */
export interface ReaderBroadcast {
  id: string
  title: string
  body: string
  authorLabel: string
  publishedAt: string
}

export const getBroadcasts = (auth: StaffAuth = {}): Promise<BroadcastView[]> =>
  get('/platform/broadcasts', auth)

export const createBroadcast = (
  body: {
    title: string
    body: string
    roles: readonly string[]
    /** Omit or send `[]` for every school. */
    schoolIds?: readonly string[]
    publish?: boolean
  },
  auth: StaffAuth = {},
): Promise<BroadcastView> => post('/platform/broadcasts', body, auth)

/** ⚠️ `POST …/withdraw`, not `DELETE` — `withdrawNotice`'s reason. */
export const withdrawBroadcast = (id: string, auth: StaffAuth = {}): Promise<BroadcastView> =>
  post(`/platform/broadcasts/${encodeURIComponent(id)}/withdraw`, {}, auth)

/**
 * `GET /broadcasts/mine` — what the signed-in reader should be shown.
 *
 * 🔴 EVERY TIER CALLS THIS. Both the role and the school that decide which
 * broadcasts come from the token: a `?role=` would hand a parent whatever an
 * operator wrote for teachers, and a `?schoolId=` would hand one school
 * another's.
 */
export const getMyBroadcasts = (auth: StaffAuth = {}): Promise<ReaderBroadcast[]> =>
  get('/broadcasts/mine', auth)

/* ─────────────────────────── change 19 §9, §11, §12 · who is using this ── */

export type EngagementBand = 'ACTIVE' | 'OCCASIONAL' | 'LAPSED' | 'NEVER'

export interface BandCounts {
  active: number
  occasional: number
  lapsed: number
  never: number
  total: number
}

export interface SchoolParentEngagement {
  schoolId: string
  name: string
  status: SchoolStatus
  parents: BandCounts
  /**
   * 🔴 ACTIVE AS A SHARE OF PARENTS EVER REACHED — `never` IS NOT IN THE
   * DENOMINATOR. A school that enrolled two hundred families last week would
   * otherwise read 1% and bury the school that genuinely lost its families.
   *
   * ⚠️ `null` MEANS NOBODY HAS EVER SIGNED IN, and is not `0`. Zero reads as
   * "they all left"; null is "there is nothing to measure yet".
   */
  reachedRate: number | null
}

export interface EngagedTeacher {
  userId: string
  name: string | null
  schoolId: string | null
  schoolName: string | null
  /** Sittings run in the window. This is what the ranking is on. */
  sessions: number
  /** Distinct children seen in it. Never a count of sessions. */
  children: number
  lastLoginAt: string | null
  band: EngagementBand
}

/**
 * Change 19 §9, §11 and §12.
 *
 * 🔴 FOUR BANDS, NOT THE THREE §9 NAMES. "Not using" collapses a family who
 * signed in once and stopped (`LAPSED` — worth a nudge, and what §14's WhatsApp
 * reminder is for) with a family nobody has ever reached (`NEVER` — a "come
 * back" message is the wrong message). Reporting them together hides which
 * problem a school has.
 */
export interface EngagementOverview {
  generatedAt: string
  /** 🔴 The two thresholds, so no figure can be rendered without its period. */
  windowDays: { active: number; occasional: number }
  /**
   * 🔴 CHANGE 19 §13 — TRUE WHEN THE READ HIT ITS CAP, WHICH MAKES EVERY FIGURE
   * BELOW A SAMPLE RATHER THAN A CENSUS. A screen that renders "62% of parents
   * are active" without saying so is stating something it does not know.
   */
  truncated: boolean
  parents: BandCounts
  /** §12 — worst first; schools with nothing to measure sort last. */
  parentsBySchool: SchoolParentEngagement[]
  teachers: BandCounts
  /** §11 — ranked by sittings run, never by logins. */
  mostEngagedTeachers: EngagedTeacher[]
  /** 🔴 A list, not a count: a number leaves an operator with nobody to ring. */
  neverSignedInTeachers: EngagedTeacher[]
}

export const getEngagement = (auth: StaffAuth = {}): Promise<EngagementOverview> =>
  get('/superadmin/engagement', auth)
